/**
 * Custom hook for managing structure panel state
 */
import { useState, useCallback } from "react"
import { StructureNode, parseQuestionnaireStructure } from "utils/structureParser"
import { EditorRef } from "types/questionnaire.types"
import { notify } from "shared/ui/Notifications/notify"

interface UseStructurePanelReturn {
  structureTree: StructureNode | null
  parseStructure: () => void
  isLoading: boolean
}

export const useStructurePanel = (
  editorRef: React.RefObject<EditorRef | null>
): UseStructurePanelReturn => {
  const [structureTree, setStructureTree] = useState<StructureNode | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const parseStructure = useCallback(() => {
    setIsLoading(true)

    try {
      if (!editorRef.current) {
        notify.error("Error", "Editor reference not available")
        return
      }

      const editorHTML = editorRef.current.getHTML()
      const text = editorHTML.replace(/<p>/g, "").replace(/<\/p>/g, "\n")

      // Unescape HTML entities
      const unescapedText = text
        .replace(/&lt;&lt;/g, "<<")
        .replace(/&gt;&gt;/g, ">>")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")

      const tree = parseQuestionnaireStructure(unescapedText)
      setStructureTree(tree)
    } catch (error) {
      console.error("Error parsing structure:", error)
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      notify.error("Structure Parse Error", errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [editorRef])

  return {
    structureTree,
    parseStructure,
    isLoading,
  }
}
